import { formatYmd } from "@/features/dashboard/lib/ymd";

/** Days back from today for each dashboard lookback. `12m` is 365 days, not calendar months. */
export const LOOKBACK_DAYS = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  "12m": 365,
} as const;

type LookbackRange = keyof typeof LOOKBACK_DAYS;

const DEFAULT_LOOKBACK_DAYS = 90;
function isLookbackRange(dateRange: string): dateRange is LookbackRange {
  return dateRange in LOOKBACK_DAYS;
}

export function lookbackDays(dateRange: string): number {
  return isLookbackRange(dateRange) ? LOOKBACK_DAYS[dateRange] : DEFAULT_LOOKBACK_DAYS;
}

export function lookbackWindow(
  dateRange: string,
  today: Date,
): { start: string; end: string } {
  const from = new Date(today);
  from.setDate(from.getDate() - (lookbackDays(dateRange) - 1));
  return { start: formatYmd(from), end: formatYmd(today) };
}

export function resolveModeDates(args: {
  changed: boolean;
  computed: { start: string; end: string };
  currentStart: string;
  currentEnd: string;
}): { start: string; end: string } {
  if (args.changed) return args.computed;
  return { start: args.currentStart, end: args.currentEnd };
}

export interface DateWindow {
  start: string;
  end: string;
}

/** Optional custom dates carried in the URL for one mode (absent → fall back to window). */
export interface UrlDateWindow {
  start?: string;
  end?: string;
}

/**
 * One mode's date selection identity.
 *
 * The `dateRange` token cannot serve as the change signal: ten of the fourteen
 * presets have no legacy token and deliberately leave it untouched, so a
 * token-keyed comparison reports `noop` and the picked range never lands.
 */
export interface ModeSelection {
  presetId: string;
  /** Empty when no explicit range has been picked for this mode yet. */
  start: string;
  end: string;
}

export function isSameSelection(
  a: ModeSelection | undefined,
  b: ModeSelection | undefined,
): boolean {
  if (a === undefined || b === undefined) return a === b;
  return a.presetId === b.presetId && a.start === b.start && a.end === b.end;
}

export interface LookbackSyncState {
  /** Drilldown URL hydration phase — `pending` until the first URL→store hydration lands. */
  phase: "pending" | "hydrated";
  /** True when this render follows a fresh external URL change (back/forward, paste). */
  isExternalHydration: boolean;
  /** Selections captured on the previous sync — `undefined` before the first seed. */
  prevCompetitorsSelection: ModeSelection | undefined;
  prevMyBrandSelection: ModeSelection | undefined;
  competitorsSelection: ModeSelection;
  myBrandSelection: ModeSelection;
  /** Token-derived fallback windows, used when a mode has no explicit range. */
  competitorsWindow: DateWindow;
  myBrandWindow: DateWindow;
  /** Currently applied dates in the store, per mode. */
  currentCompetitors: DateWindow;
  currentMyBrand: DateWindow;
  /** Custom dates currently present in the URL, per mode. */
  urlCompetitors: UrlDateWindow;
  urlMyBrand: UrlDateWindow;
}

/** The mode's explicit picked range, falling back to its token-derived window. */
function selectionWindow(selection: ModeSelection, fallback: DateWindow): DateWindow {
  if (selection.start && selection.end) {
    return { start: selection.start, end: selection.end };
  }
  return fallback;
}

/** URL dates win only as a complete pair — a one-sided link must not mix with the fallback. */
function urlWindowOrFallback(url: UrlDateWindow, fallback: DateWindow): DateWindow {
  if (url.start && url.end) {
    return { start: url.start, end: url.end };
  }
  return fallback;
}

export type LookbackSyncAction =
  | { type: "initialize"; competitors: DateWindow; myBrand: DateWindow }
  | { type: "seed" }
  | { type: "noop" }
  | { type: "apply"; competitors: DateWindow; myBrand: DateWindow; pushUrl: boolean };

/**
 * Decide how the Drilldown date-sync effect should react to a lookback/URL change.
 *
 * - `initialize` (deep link): URL dates win, falling back to each mode's computed window.
 * - `seed`: capture the current lookbacks as the baseline without touching dates — used on
 *   the first post-hydration run AND on external history navigation, so restored custom
 *   dates are never clobbered by a recompute.
 * - `noop`: neither lookback actually changed.
 * - `apply`: a real user lookback change — recompute the changed mode(s) and (when the
 *   resolved dates differ from the URL) ask the caller to push them.
 */
export function resolveLookbackSync(state: LookbackSyncState): LookbackSyncAction {
  if (state.phase === "pending") {
    return {
      type: "initialize",
      competitors: urlWindowOrFallback(
        state.urlCompetitors,
        selectionWindow(state.competitorsSelection, state.competitorsWindow),
      ),
      myBrand: urlWindowOrFallback(
        state.urlMyBrand,
        selectionWindow(state.myBrandSelection, state.myBrandWindow),
      ),
    };
  }

  if (
    state.prevCompetitorsSelection === undefined &&
    state.prevMyBrandSelection === undefined
  ) {
    return { type: "seed" };
  }

  // History navigation restored custom dates alongside a possibly-different lookback —
  // re-baseline the lookbacks and let the restored URL dates stand.
  if (state.isExternalHydration) {
    return { type: "seed" };
  }

  const changedCompetitors = !isSameSelection(
    state.prevCompetitorsSelection,
    state.competitorsSelection,
  );
  const changedMyBrand = !isSameSelection(state.prevMyBrandSelection, state.myBrandSelection);
  if (!changedCompetitors && !changedMyBrand) return { type: "noop" };

  const competitors = resolveModeDates({
    changed: changedCompetitors,
    computed: selectionWindow(state.competitorsSelection, state.competitorsWindow),
    currentStart: state.currentCompetitors.start,
    currentEnd: state.currentCompetitors.end,
  });
  const myBrand = resolveModeDates({
    changed: changedMyBrand,
    computed: selectionWindow(state.myBrandSelection, state.myBrandWindow),
    currentStart: state.currentMyBrand.start,
    currentEnd: state.currentMyBrand.end,
  });

  const pushUrl =
    (changedCompetitors &&
      (state.urlCompetitors.start !== competitors.start ||
        state.urlCompetitors.end !== competitors.end)) ||
    (changedMyBrand &&
      (state.urlMyBrand.start !== myBrand.start || state.urlMyBrand.end !== myBrand.end));

  return { type: "apply", competitors, myBrand, pushUrl };
}
